import { useState, useEffect } from 'react';
import type { BenchmarkRun } from '../../types/benchmark';
import { TagBadge } from './TagBadge';

interface TagsFilterProps {
  runs: BenchmarkRun[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

export function TagsFilter({ runs, selectedTags, onTagsChange }: TagsFilterProps) {
  const [allTags, setAllTags] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Extract all unique tags from runs
    const tags = new Set<string>();
    runs.forEach(run => {
      run.tags.forEach(tag => tags.add(tag));
    });
    setAllTags(Array.from(tags).sort());
  }, [runs]);

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const clearTags = () => {
    onTagsChange([]);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-4 py-2 border rounded bg-white hover:bg-gray-50 flex items-center gap-2"
      >
        <span>Filtrer par tag</span>
        {selectedTags.length > 0 && (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
            {selectedTags.length}
          </span>
        )}
        <span className="text-gray-400">▼</span>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 bg-white border rounded shadow-lg z-10 p-4 min-w-64">
          <div className="flex justify-between items-center mb-3">
            <span className="font-medium text-sm">Tags disponibles</span>
            {selectedTags.length > 0 && (
              <button
                onClick={clearTags}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Tout effacer
              </button>
            )}
          </div>

          {allTags.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun tag disponible</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {allTags.map((tag) => (
                <label key={tag} className="flex items-center hover:bg-gray-50 p-2 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag)}
                    onChange={() => toggleTag(tag)}
                    className="mr-3"
                  />
                  <span className="text-sm">{tag}</span>
                </label>
              ))}
            </div>
          )}

          <div className="mt-3 pt-3 border-t">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full px-4 py-2 bg-gray-100 rounded hover:bg-gray-200 text-sm"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {selectedTags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedTags.map((tag) => (
            <TagBadge key={tag} tag={tag} onRemove={() => toggleTag(tag)} />
          ))}
        </div>
      )}
    </div>
  );
}
